import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Lock, Mail, ArrowRight, ArrowLeft, Crown } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import CinematicBackground from '../components/CinematicBackground';

function formatDetail(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || String(e)).join(' ');
  return detail?.msg || String(detail);
}

export default function AdminLogin() {
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post('/auth/login', {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        portal: 'admin',
      });
      toast.success('Owner access granted');
      await checkAuth();
      nav('/admin/dashboard', { replace: true });
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#040A08] text-foreground overflow-hidden" data-testid="admin-login-root">
      <CinematicBackground intensity="dim" />
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        <motion.button
          onClick={() => nav('/')}
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          data-testid="btn-admin-back"
          className="absolute top-6 left-6 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-400/30 text-[10px] uppercase tracking-[0.3em] text-amber-300">
              <Crown className="w-3 h-3" /> Owner Access
            </div>
            <h1 className="mt-4 font-serif text-4xl leading-tight">Admin Sign-In</h1>
            <p className="mt-2 text-xs text-muted-foreground max-w-xs mx-auto">
              Restricted to accounts with administrator privileges. All other credentials will be rejected.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3 p-6 rounded-3xl glass border border-white/10" data-testid="admin-login-form">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-1.5">
                <Mail className="w-3 h-3" /> Email
              </span>
              <input
                data-testid="admin-input-email"
                type="email" autoComplete="username" required
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@wildquest.com"
                className="w-full px-4 py-2.5 rounded-full bg-black/30 border border-white/10 text-sm outline-none focus:border-amber-400/60"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-1.5">
                <Lock className="w-3 h-3" /> Password
              </span>
              <input
                data-testid="admin-input-password"
                type="password" autoComplete="current-password" required
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 rounded-full bg-black/30 border border-white/10 text-sm outline-none focus:border-amber-400/60"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-submit"
              className="w-full py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-50 hover:brightness-110"
            >
              <ShieldCheck className="w-4 h-4" />
              {busy ? 'Verifying…' : 'Access Admin Portal'}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center justify-between pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
              <button type="button"
                data-testid="admin-forgot"
                onClick={() => nav('/forgot-password')}
                className="hover:text-foreground">Forgot password?</button>
              <span className="text-[9px]">Bootstrap admin protected</span>
            </div>
          </form>

          <p className="text-center mt-6 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            All admin actions are logged in the audit trail.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
